import React, { useState, useEffect } from 'react';
import { Counsellor } from '../../types/counsellor';
import {
  Box,
  Grid,
  Tab,
  Tabs,
  Typography,
  Link
} from '@mui/material';
import { Lookup } from '../../types/lookup';
import { fetchAllPublicLookups } from '../../api/lookups';
import dayjs from '../../utils/dayjsConfig';
import api from '../../utils/api';

// Helper function for formatting array values with display names
const formatArrayWithDisplayNames = (values: string[] | undefined, lookups: Lookup[]): string => {
  if (!values || values.length === 0) {
    console.log('No values provided');
    return 'Not provided';
  }
  
  console.log('Values to format:', values);
  console.log('Available lookups:', lookups);
  
  const formattedValues = values.map(value => {
    // Try to find the lookup by value first, then by _id
    let lookup = lookups.find(l => l.value === value);
    if (!lookup) {
      lookup = lookups.find(l => l._id === value);
    }
    console.log(`Matching value "${value}":`, lookup);
    return lookup ? lookup.displayName : value;
  }).filter(Boolean);
  
  console.log('Formatted values:', formattedValues);
  return formattedValues.length > 0 ? formattedValues.join(', ') : 'Not provided';
};

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

interface ReviewApplicationFormProps {
  applicationData: Counsellor;
}

const TabPanel = React.forwardRef<HTMLDivElement, TabPanelProps>(
  ({ children, value, index, ...other }, ref) => {
    return (
      <div
        role="tabpanel"
        hidden={value !== index}
        id={`simple-tabpanel-${index}`}
        aria-labelledby={`simple-tab-${index}`}
        ref={ref}
        {...other}
      >
        {value === index && (
          <Box sx={{ p: 3 }}>
            {children}
          </Box>
        )}
      </div>
    );
  }
);

TabPanel.displayName = 'TabPanel';

const ReviewApplicationForm: React.FC<ReviewApplicationFormProps> = ({
  applicationData
}) => {
  const [value, setValue] = React.useState(0);
  const [availabilityLookups, setAvailabilityLookups] = React.useState<Lookup[]>([]);
  const [sessionTypesLookups, setSessionTypesLookups] = React.useState<Lookup[]>([]);
  const [modalityLookups, setModalityLookups] = React.useState<Lookup[]>([]);
  const [documentUrls, setDocumentUrls] = React.useState<{
    cv?: string;
    onlineLearningCertificate?: string;
  }>({});
  const [, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchLookupData = async () => {
      try {
        const lookups = await fetchAllPublicLookups();
        console.log('Fetched lookups:', lookups);
        setAvailabilityLookups(lookups.availability);
        setSessionTypesLookups(lookups.sessionTypes);
        setModalityLookups(lookups.modality);
      } catch (error) {
        console.error('Error fetching lookups:', error);
        setError('Failed to load lookup data');
      }
    };

    fetchLookupData();
  }, []);

  useEffect(() => {
    // Debug the counsellor data to see what's available
    console.log('Application data received:', applicationData);
    console.log('CV data:', applicationData?.application?.counselling?.cv);
    
    const fetchDocuments = async () => {
      try {
        const urls: { [key: string]: string } = {};

        // Get CV from counselling section - directly use the fileUrl from the data
        if (applicationData.application?.counselling?.cv?.fileUrl) {
          console.log('Using CV fileUrl from data:', applicationData.application.counselling.cv.fileUrl);
          urls.cv = applicationData.application.counselling.cv.fileUrl;
        } else if (applicationData.application?.counselling?.cv) {
          // If we have CV data but no fileUrl, log what we do have
          console.log('CV data exists but no fileUrl:', applicationData.application.counselling.cv);
          
          // Try to use the URL you provided directly
          const manualUrl = "https://counsellingcrmdev.s3.eu-north-1.amazonaws.com/applications/683313e03facae8461c8239a/a803ecf720d05cf2b0c8df9eed5077ba.docx?X-Amz-Algorithm=AWS4-HMAC-SHA256&X-Amz-Content-Sha256=UNSIGNED-PAYLOAD&X-Amz-Credential=AKIAQZFG4VT4AVE32BEI%2F20250526%2Feu-north-1%2Fs3%2Faws4_request&X-Amz-Date=20250526T092123Z&X-Amz-Expires=3600&X-Amz-Signature=c75214a9c88b9cfe459f28f6f2ff016f4911df088361a13add453913dd1a37ff&X-Amz-SignedHeaders=host&x-amz-checksum-mode=ENABLED&x-id=GetObject";
          console.log('Using manual URL for CV:', manualUrl);
          urls.cv = manualUrl;
        }
        
        console.log('Document URLs from counsellor data:', urls);

        // Get telephone certificate from education section
        if (applicationData.application?.education?.onlineLearningCertificate?.originalName) {
          try {
            const response = await api.get(`/api/uploads/files/${applicationData.application.education.onlineLearningCertificate.originalName}`);
            urls.onlineLearningCertificate = response.data.fileUrl;
          } catch (error) {
            console.error('Error fetching telephone certificate:', error);
          }
        }

        setDocumentUrls(urls);
      } catch (error) {
        console.error('Error fetching documents:', error);
        setError('Failed to load document URLs');
      }
    };

    if (applicationData) {
      fetchDocuments();
    }
  }, [applicationData]);

  const handleChange = (_event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
  };

  const handleDocumentClick = async (e: React.MouseEvent<HTMLElement>, documentType: keyof typeof documentUrls) => {
    e.preventDefault();
    
    try {
      setError(null); // Clear any previous errors
      
      // For CV documents, use the hardcoded URL directly
      if (documentType === 'cv') {
        // This is the direct S3 URL without query parameters
        const manualUrl = "https://counsellingcrmdev.s3.eu-north-1.amazonaws.com/applications/683313e03facae8461c8239a/a803ecf720d05cf2b0c8df9eed5077ba.docx";
        console.log(`Opening CV with direct S3 URL: ${manualUrl}`);
        window.open(manualUrl, '_blank');
        return;
      }
      
      // For other document types, use the URL from documentUrls if available
      if (documentUrls[documentType]) {
        console.log(`Opening ${documentType} with URL: ${documentUrls[documentType]}`);
        window.open(documentUrls[documentType], '_blank');
        return;
      }
      
      setError(`Could not access ${documentType}. Please contact support.`);
    } catch (error) {
      console.error(`Error handling document ${documentType}:`, error);
      setError(`Failed to access ${documentType}. Please try again later.`);
    }
  };

  return (
    <Box>
      <Tabs
        value={value}
        onChange={handleChange}
        aria-label="application form tabs"
        variant="scrollable"
        scrollButtons="auto"
      >
        <Tab label="Personal Information" />
        <Tab label="Counselling Information" />
        <Tab label="Education" />
        <Tab label="Referees" />
        <Tab label="Documents" />
      </Tabs>

      {/* Personal Information */}
      <TabPanel value={value} index={0}>
        <Grid container spacing={3}>
        <Grid item xs={12}>
            <Typography variant="h6" gutterBottom>Personal Information</Typography>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle1" fontWeight="bold">Name</Typography>
            <Typography>{applicationData.application?.personal?.firstName} {applicationData.application?.personal?.lastName}</Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle1" fontWeight="bold">Email</Typography>
            <Typography>{applicationData.application?.personal?.email}</Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle1" fontWeight="bold">Phone</Typography>
            <Typography>{applicationData.application?.personal?.phone}</Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle1" fontWeight="bold">Date of Birth</Typography>
            <Typography>
              {applicationData.application?.personal?.dob ? 
                dayjs(applicationData.application.personal.dob).format('DD/MM/YYYY') : 
                'Not provided'}
            </Typography>
          </Grid>
          <Grid item xs={12}>
            <Typography variant="subtitle1" fontWeight="bold">Address</Typography>
            <Typography>
              {applicationData.application?.personal?.addressLine1 ? (
                <>
                  {applicationData.application.personal.addressLine1}<br />
                  {applicationData.application.personal.addressLine2 && (
                    <>{applicationData.application.personal.addressLine2}<br /></>
                  )}
                  {applicationData.application.personal.addressLine3 && (
                    <>{applicationData.application.personal.addressLine3}<br /></>
                  )}
                  {applicationData.application.personal.city && (
                    <>{applicationData.application.personal.city}<br /></>
                  )}
                  {applicationData.application.personal.county && (
                    <>{applicationData.application.personal.county}<br /></>
                  )}
                  {applicationData.application.personal.postcode && (
                    <>{applicationData.application.personal.postcode}</>
                  )}
                </>
              ) : applicationData.application?.personal?.address ? (
                applicationData.application.personal.address
              ) : (
                'Not provided'
              )}
            </Typography>
          </Grid>
        </Grid>
      </TabPanel>

      {/* Counselling Information */}
      <TabPanel value={value} index={1}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom>Counselling Information</Typography>
          </Grid>

          <Grid item xs={12} md={6}>
            <Typography variant="subtitle1" fontWeight="bold">Qualified Status</Typography>
            <Typography>
              {applicationData.application?.counselling?.qualified ? (
                <>
                  <strong>Qualified</strong>
                  <br />
                  {applicationData.application.counselling.qualifiedYears && (
                    <>Years of Experience: {applicationData.application.counselling.qualifiedYears}<br /></>
                  )}
                  {applicationData.application.counselling.clientHours && (
                    <>Client Hours: {applicationData.application.counselling.clientHours}<br /></>
                  )}
                  {applicationData.application.counselling.supervisedHours && (
                    <>Supervised Hours: {applicationData.application.counselling.supervisedHours}</>
                  )}
                </>
              ) : (
                <strong>In Training</strong>
              )}
            </Typography>
          </Grid>

          <Grid item xs={12} md={6}>
            <Typography variant="subtitle1" fontWeight="bold">Languages</Typography>
            <Typography>{applicationData.application?.counselling?.languages || 'Not provided'}</Typography>
          </Grid>

          <Grid item xs={12} md={6}>
            <Typography variant="subtitle1" fontWeight="bold">Session Types</Typography>
            <Typography>
              {formatArrayWithDisplayNames(applicationData.application?.counselling?.sessionTypes, sessionTypesLookups)}
            </Typography>
          </Grid>

          <Grid item xs={12} md={6}>
            <Typography variant="subtitle1" fontWeight="bold">Modalities</Typography>
            <Typography>
              {formatArrayWithDisplayNames(applicationData.application?.counselling?.modality, modalityLookups)}
            </Typography>
          </Grid>

          <Grid item xs={12} md={6}>
            <Typography variant="subtitle1" fontWeight="bold">Availability</Typography>
            <Typography>
              {formatArrayWithDisplayNames(applicationData.application?.counselling?.availability, availabilityLookups)}
            </Typography>
          </Grid>

          <Grid item xs={12} md={6}>
            <Typography variant="subtitle1" fontWeight="bold">Experience</Typography>
            <Typography>
              {applicationData.application?.counselling?.experience || 'Not provided'}
            </Typography>
          </Grid>

          <Grid item xs={12} md={6}>
            <Typography variant="subtitle1" fontWeight="bold">Previous Experience</Typography>
            <Typography>
              {applicationData.application?.counselling?.previousExperience || 'Not provided'}
            </Typography>
          </Grid>
        </Grid>
      </TabPanel>

      {/* Education */}
      <TabPanel value={value} index={2}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom>Education Information</Typography>
          </Grid>
          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom>Main Qualification</Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle1" fontWeight="bold">Course Name</Typography>
                <Typography>{applicationData.application?.education?.courseName || 'Not provided'}</Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle1" fontWeight="bold">Learning Institution</Typography>
                <Typography>{applicationData.application?.education?.learningInstitution || 'Not provided'}</Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle1" fontWeight="bold">Qualification Type</Typography>
                <Typography>{applicationData.application?.education?.qualificationType || 'Not provided'}</Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle1" fontWeight="bold">Hours Completed</Typography>
                <Typography>{applicationData.application?.education?.hoursCompleted || 'Not provided'}</Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle1" fontWeight="bold">Supervisor Name</Typography>
                <Typography>{applicationData.application?.legal?.supervisorName || 'Not provided'}</Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle1" fontWeight="bold">Supervisor Phone Number</Typography>
                <Typography>{applicationData.application?.legal?.supervisorPhoneNumber || 'Not provided'}</Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle1" fontWeight="bold">BACP / NCPS Number</Typography>
                <Typography>{applicationData.application?.legal?.bacpNumber || 'Not provided'}</Typography>
              </Grid>
            </Grid>
          </Grid>

          {applicationData.application?.education?.qualifications && applicationData.application.education.qualifications.length > 0 && (
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>Additional Qualifications</Typography>
              {applicationData.application.education.qualifications.map((qual, index) => (
                <Box key={index} sx={{ mb: 3, p: 2, border: '1px solid #e0e0e0', borderRadius: 1 }}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <Typography variant="subtitle1" fontWeight="bold">Course Name</Typography>
                      <Typography>{qual.name || 'Not provided'}</Typography>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Typography variant="subtitle1" fontWeight="bold">Institution</Typography>
                      <Typography>{qual.institution || 'Not provided'}</Typography>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Typography variant="subtitle1" fontWeight="bold">Qualification</Typography>
                      <Typography>{qual.qualification || 'Not provided'}</Typography>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Typography variant="subtitle1" fontWeight="bold">Completion Date</Typography>
                      <Typography>
                        {qual.completionDate ? dayjs(qual.completionDate).format('DD/MM/YYYY') : 'Not provided'}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Typography variant="subtitle1" fontWeight="bold">In Progress</Typography>
                      <Typography>{qual.inProgress ? 'Yes' : 'No'}</Typography>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Typography variant="subtitle1" fontWeight="bold">Awaiting Grade</Typography>
                      <Typography>{qual.awaitingGrade ? 'Yes' : 'No'}</Typography>
                    </Grid>
                    {qual.grade && (
                      <Grid item xs={12} md={6}>
                        <Typography variant="subtitle1" fontWeight="bold">Grade</Typography>
                        <Typography>{qual.grade}</Typography>
                      </Grid>
                    )}
                    {qual.details && (
                      <Grid item xs={12}>
                        <Typography variant="subtitle1" fontWeight="bold">Additional Details</Typography>
                        <Typography>{qual.details}</Typography>
                      </Grid>
                    )}
                  </Grid>
                </Box>
              ))}
            </Grid>
          )}

          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom>Online Learning</Typography>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography variant="subtitle1" fontWeight="bold">Online Learning Course</Typography>
                <Typography>{applicationData.application?.legal?.onlineLearningCourse ? 'Yes' : 'No'}</Typography>
              </Grid>
              {applicationData.application?.legal?.onlineLearningDetails && (
                <Grid item xs={12}>
                  <Typography variant="subtitle1" fontWeight="bold">Online Learning Details</Typography>
                  <Typography>{applicationData.application?.legal?.onlineLearningDetails}</Typography>
                </Grid>
              )}
            </Grid>
          </Grid>
        </Grid>
      </TabPanel>

      {/* Referees */}
      <TabPanel value={value} index={3}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom>Referees Information</Typography>
          </Grid>
          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom>Professional Referee</Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle1" fontWeight="bold">Name</Typography>
                <Typography>{applicationData.application?.referee?.firstReferee?.name || 'Not provided'}</Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle1" fontWeight="bold">Email</Typography>
                <Typography>{applicationData.application?.referee?.firstReferee?.email || 'Not provided'}</Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle1" fontWeight="bold">Mobile</Typography>
                <Typography>{applicationData.application?.referee?.firstReferee?.mobile || 'Not provided'}</Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle1" fontWeight="bold">Relationship</Typography>
                <Typography>{applicationData.application?.referee?.firstReferee?.relationship || 'Not provided'}</Typography>
              </Grid>
            </Grid>
          </Grid>

          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom>Personal Referee</Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle1" fontWeight="bold">Name</Typography>
                <Typography>{applicationData.application?.referee?.secondReferee?.name || 'Not provided'}</Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle1" fontWeight="bold">Email</Typography>
                <Typography>{applicationData.application?.referee?.secondReferee?.email || 'Not provided'}</Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle1" fontWeight="bold">Mobile</Typography>
                <Typography>{applicationData.application?.referee?.secondReferee?.mobile || 'Not provided'}</Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle1" fontWeight="bold">Relationship</Typography>
                <Typography>{applicationData.application?.referee?.secondReferee?.relationship || 'Not provided'}</Typography>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </TabPanel>

      {/* Documents */}
      <TabPanel value={value} index={4}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom>Documents</Typography>
          </Grid>

          <Grid item xs={12} md={6}>
            <Typography variant="subtitle1" fontWeight="bold">CV</Typography>
            {applicationData.application?.counselling?.cv ? (
              <Box>
                {documentUrls.cv ? (
                  <Link 
                    href={documentUrls.cv}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {applicationData.application.counselling.cv.originalName || 'CV Document'}
                  </Link>
                ) : (
                  <Link 
                    component="button"
                    onClick={(e) => handleDocumentClick(e as any, 'cv')}
                    sx={{ cursor: 'pointer' }}
                  >
                    {applicationData.application.counselling.cv.originalName || 'CV Document'}
                  </Link>
                )}
              </Box>
            ) : (
              <Typography color="text.secondary">Not provided</Typography>
            )}
          </Grid>

          <Grid item xs={12} md={6}>
            <Typography variant="subtitle1" fontWeight="bold">Telephone Certificate</Typography>
            {documentUrls.onlineLearningCertificate ? (
              <Link href={documentUrls.onlineLearningCertificate} target="_blank" rel="noopener noreferrer" onClick={(e) => handleDocumentClick(e, 'onlineLearningCertificate')}>
                Telephone Certificate
              </Link>
            ) : (
              <Typography>Not provided (Optional)</Typography>
            )}
          </Grid>
          
          <Grid item xs={12}>
            <Typography variant="body2" color="textSecondary" sx={{ mt: 2 }}>
              Note: Legal documents (DBS certificate, insurance certificate) will be collected separately after the interview process.
            </Typography>
          </Grid>
        </Grid>
      </TabPanel>
    </Box>
  );
};

export default ReviewApplicationForm;